import { APIError, Logger, type ResponseSuccess, isResponseError, type TerrosHeaderMetadata } from '@terros/common'
import { HTTPStatuses } from './types'
import { getRequestOptions } from './getRequestOptions'
import { type BackendContextType, type Stage } from './BackendContext'

const logger = new Logger('api-core')
// logger.level = 'verbose'

export type ServerRequestProps<I> = {
  backend: BackendContextType
  path: string
  additionalHeaders?: TerrosHeaderMetadata
  input?: I
}

export async function serverRequest<I, O extends ResponseSuccess>({
  backend,
  path,
  input,
  additionalHeaders,
}: ServerRequestProps<I>): Promise<O> {
  const fullURL = getApiUrl(backend.getStage()) + path

  try {
    const options = await getRequestOptions({ backend, fullURL, input, additionalHeaders })
    const response = await fetch(fullURL, options)
    const data = await response.json()

    logger.verbose('response', response.status, data)

    if (!response.ok) {
      const statusText = HTTPStatuses[response.status] ?? 'Unknown server error'
      throw new APIError({
        type: 'error',
        error: 'Server',
        message: statusText,
        retry: false,
      })
    }

    if (isResponseError(data)) throw new APIError(data)

    return data as O
  } catch (error) {
    if (error instanceof TypeError) {
      // Handle network errors (e.g., no internet, server unreachable, timeout)
      logger.error(`Network error for ${fullURL}:`, error)
      throw new APIError({ type: 'error', error: 'Network' })
    }

    if (error instanceof SyntaxError) {
      logger.error(`Syntax error for ${fullURL}:`, error)
      throw new APIError({ type: 'error', error: 'Unknown server error' })
    }

    logger.warn(`Request failed for ${fullURL}:`, error)
    if (error instanceof APIError) throw error
    throw new APIError({
      type: 'error',
      error: 'Server',
      message: error instanceof Error ? error.message : String(error),
      retry: false,
    })
  } finally {
    logger.verbose('Request completed for', fullURL)
  }
}

export function getApiUrl(stage: Stage): string {
  let baseUrl = 'terros.com'
  baseUrl = stage === 'prod' ? baseUrl : `${stage}.${baseUrl}`
  return `https://api.${baseUrl}`
}

/** The stage's hosted MCP address (`mcpResourceUrl` in apps/infra), the URL AI clients connect to. */
export function getMcpUrl(stage: Stage): string {
  const domain = stage === 'prod' ? 'terros.com' : `${stage}.terros.com`
  return `https://mcp.${domain}/mcp`
}
